import './styles/global.css';
import { supabase } from './lib/supabase';
import { Router, hashPath } from './lib/router';
import { takeReturnTo, peekReturnTo } from './lib/return-to';
import { readAuthLanding, isAuthCallbackHash, friendlyAuthError } from './lib/auth-landing';
import { renderHome } from './pages/home';
import { renderSignin } from './pages/signin';
import { renderApply } from './pages/apply';
import { renderClaim } from './pages/claim';
import { renderEdit } from './pages/edit';
import { renderAdmin } from './pages/admin';
import { renderRecordingTips } from './pages/help-recording-tips';
import { renderSubstrate } from './pages/substrate';

// Snapshot the URL BEFORE anything else runs. supabase-js consumes the auth
// fragment and cleans the URL on its own schedule, so by the time
// onAuthStateChange fires the evidence may be gone. Read it once, up front, and
// never ask the live URL again.
const LANDING = readAuthLanding(window.location.hash, window.location.search);
const LANDED_ON_CALLBACK = isAuthCallbackHash(window.location.hash);

const router = new Router({
  '/': renderHome,
  '/signin': renderSignin,
  '/apply': renderApply,
  '/claim': renderClaim,
  '/edit': renderEdit,
  '/admin': renderAdmin,
  '/help/recording-tips': renderRecordingTips,
  // Operator door (Saoirse Console) — superAdmin-gated inside the page.
  '/substrate': renderSubstrate,
});

/**
 * A sign-in link that failed.
 *
 * John reopened a spent link and the site rendered NOTHING — the home page with
 * `?error=access_denied&error_code=otp_expired` sitting unread in the URL. A
 * dead end with no way forward is worse than an error page. `peekReturnTo` here,
 * not `take`: the user has not signed in, so the route they were headed for must
 * survive for the next attempt.
 */
function renderAuthError(): void {
  const root = document.getElementById('app');
  if (!root) return;
  const back = peekReturnTo() ?? '#/signin';
  const backLabel = back.startsWith('#/claim') ? 'Try claiming your business again'
    : back.startsWith('#/admin') ? 'Back to the operator sign-in'
    : back.startsWith('#/apply') ? 'Back to the application'
    : 'Request a new sign-in link';
  root.innerHTML = `
    <main class="cl-shell">
      <div class="cl-card">
        <div class="cl-logo">Curate</div>
        <h1>That link didn't work</h1>
        <p class="cl-lead">${friendlyAuthError(LANDING).replace(/</g, '&lt;')}</p>
        <a class="cl-btn" href="${back.replace(/"/g, '&quot;')}">${backLabel}</a>
        <p class="cl-foot">Still stuck? Email john@ebikesflorence.com.</p>
      </div>
    </main>
  `;
}

if (LANDING.kind === 'error') {
  // Do not start the router: it would render whatever route the mangled hash
  // resolved to (home) and bury the reason.
  renderAuthError();
} else {
  router.start();
}

// Post-auth navigation. The magic link can only come back to the bare origin
// (Supabase strips fragments from redirect-allowlist matching), so the route the
// user actually wanted is stashed in localStorage and restored here.
//
// The landing hash is now ALSO a trigger. Under the implicit flow the browser
// arrives at `#access_token=…`, which is neither '/' nor '/signin', so the old
// early-return left the user parked on the fallback route. LANDED_ON_CALLBACK is
// read from the snapshot above rather than the live URL, because supabase-js may
// already have cleaned the fragment by the time this fires.
supabase.auth.onAuthStateChange((event, session) => {
  if (event !== 'SIGNED_IN' || !session) return;
  const current = hashPath();
  const atLanding = current === '/' || current === '/signin' || LANDED_ON_CALLBACK;
  if (!atLanding) return;
  // takeReturnTo consumes, which also stops a stale value from bouncing a later
  // visit (REVIEW N10) — every successful landing now clears it.
  window.location.hash = takeReturnTo() ?? '#/edit';
});
